'use client';

import type { AuthState } from '../../types';

import { useSetState } from 'minimal-shared/hooks';
import { useMemo, useEffect, useCallback } from 'react';

import axios, { endpoints } from 'src/lib/axios';

import { JWT_STORAGE_KEY } from './constant';
import { AuthContext } from '../auth-context';
import { jwtDecode, setSession, isValidToken } from './utils';

// ----------------------------------------------------------------------

type Props = {
  children: React.ReactNode;
};

/**
 * Phase 24 — JWT now carries a `permissions: string[]` claim
 * (`pkg/tokens/tokens.go`). The legacy `roles[]` claim has been dropped.
 * `/v1/users/me` still returns `roles[]` for display / audit reasons,
 * which we surface as `user.role` (singular, first element) so existing
 * UI components that just want a label keep working without forcing a
 * permission-set → role-name reverse lookup. RBAC checks must use
 * `user.permissions` via `useHasPermission` / `useHasAnyPermission`.
 */
type JwtClaims = {
  permissions?: unknown;
  org_id?: unknown;
  org_role?: unknown;
};

function readPermissionsFromToken(token: string): string[] {
  try {
    const decoded = jwtDecode(token) as JwtClaims | null;
    if (!decoded) return [];
    const raw = decoded.permissions;
    if (!Array.isArray(raw)) return [];
    return raw.filter((p): p is string => typeof p === 'string');
  } catch {
    return [];
  }
}

// `/v1/users/me` does not include org membership — the JWT `org_id` claim is
// the only client-side source. Components read it as `user.orgId` (dashboard
// summary, billing profile, tier selector).
function readOrgIdFromToken(token: string): string | undefined {
  try {
    const decoded = jwtDecode(token) as JwtClaims | null;
    const raw = decoded?.org_id;
    return typeof raw === 'string' && raw.length > 0 ? raw : undefined;
  } catch {
    return undefined;
  }
}

function readOrgRoleFromToken(token: string): string | undefined {
  try {
    const decoded = jwtDecode(token) as JwtClaims | null;
    const raw = decoded?.org_role;
    return typeof raw === 'string' && raw.length > 0 ? raw : undefined;
  } catch {
    return undefined;
  }
}

export function AuthProvider({ children }: Props) {
  const { state, setState } = useSetState<AuthState>({ user: null, loading: true });

  const checkUserSession = useCallback(async () => {
    try {
      const accessToken = sessionStorage.getItem(JWT_STORAGE_KEY);

      if (accessToken && isValidToken(accessToken)) {
        setSession(accessToken);

        const res = await axios.get(endpoints.auth.me);
        const user = res.data;
        const permissions = readPermissionsFromToken(accessToken);
        const tokenOrgId = readOrgIdFromToken(accessToken);
        const tokenOrgRole = readOrgRoleFromToken(accessToken);
        setState({
          user: { ...user, accessToken, permissions, orgId: tokenOrgId, orgRole: tokenOrgRole },
          loading: false,
        });
      } else {
        // Token missing or expired — attempt silent refresh via httpOnly cookie
        try {
          const refreshRes = await axios.post<{ access_token: string }>(endpoints.auth.refresh);
          const newToken = refreshRes.data?.access_token;
          if (!newToken) throw new Error('No token');
          await setSession(newToken);
          const meRes = await axios.get(endpoints.auth.me);
          const user = meRes.data;
          const permissions = readPermissionsFromToken(newToken);
          const tokenOrgId = readOrgIdFromToken(newToken);
          const tokenOrgRole = readOrgRoleFromToken(newToken);
          setState({
            user: { ...user, accessToken: newToken, permissions, orgId: tokenOrgId, orgRole: tokenOrgRole },
            loading: false,
          });
        } catch {
          setState({ user: null, loading: false });
        }
      }
    } catch (error) {
      console.error(error);
      setState({ user: null, loading: false });
    }
  }, [setState]);

  useEffect(() => {
    checkUserSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ----------------------------------------------------------------------

  const checkAuthenticated = state.user ? 'authenticated' : 'unauthenticated';

  const status = state.loading ? 'loading' : checkAuthenticated;

  const memoizedValue = useMemo(() => {
    // Phase 24 (was Phase 22 BL-PANEL-AUTH-ROLE-MAPPING): the backend
    // `/v1/users/me` endpoint still returns `roles: string[]`. We pick
    // the first role as a display label so legacy UI bits that read
    // `user.role` keep rendering. RBAC gating must NOT depend on this
    // — use `permissions` (resolved server-side from `role_permissions`)
    // via `useHasPermission` / `useHasAnyPermission`.
    const rawUser = state.user as
      | (Record<string, unknown> & { roles?: unknown; role?: unknown; permissions?: unknown })
      | null;

    let displayRole: string | undefined;
    if (rawUser) {
      if (typeof rawUser.role === 'string' && rawUser.role.length > 0) {
        displayRole = rawUser.role;
      } else if (Array.isArray(rawUser.roles)) {
        const first = rawUser.roles.find((r): r is string => typeof r === 'string');
        displayRole = first;
      }
    }

    const permissions =
      rawUser && Array.isArray(rawUser.permissions)
        ? (rawUser.permissions.filter((p): p is string => typeof p === 'string') as string[])
        : [];

    return {
      user: rawUser
        ? {
            ...rawUser,
            role: displayRole,
            permissions,
          }
        : null,
      permissions,
      checkUserSession,
      loading: status === 'loading',
      authenticated: status === 'authenticated',
      unauthenticated: status === 'unauthenticated',
    };
  }, [checkUserSession, state.user, status]);

  return <AuthContext value={memoizedValue}>{children}</AuthContext>;
}
